<template>
  <Modal
    class="roles"
    v-if="modals.roles && nonTravellers >= 5"
    @close="toggleModal('roles')"
  >
    <h3>Select the characters for {{ nonTravellers }} players:</h3>
    <ul class="tokens" v-for="(teamRoles, team) in roleSelection" :key="team">
      <li class="count" :class="[team]">
        {{ teamRoles.reduce((a, { selected }) => a + selected, 0) }} /
        {{ game[nonTravellers - 5][team] }}
      </li>
      <li
        v-for="role in teamRoles"
        :class="[
          {
            selected: role.selected,
            disabled: isDisabled(role),
          },
          role.team,
        ]"
        :key="role.id"
        @click="role.selected = role.selected ? 0 : 1"
      >
        <Token :role="role" />
        <font-awesome-icon
          icon="exclamation-triangle"
          v-if="role.setup || isDisabled(role)"
        />
        <div class="buttons" v-if="allowMultiple">
          <font-awesome-icon
            icon="minus-circle"
            @click.stop="role.selected--"
          />
          <span>{{ role.selected > 1 ? "x" + role.selected : "" }}</span>
          <font-awesome-icon icon="plus-circle" @click.stop="role.selected++" />
        </div>
      </li>
    </ul>
    <label class="multiple" :class="{ checked: allowMultiple }">
      <font-awesome-icon :icon="allowMultiple ? 'check-square' : 'square'" />
      <input type="checkbox" name="allow-multiple" v-model="allowMultiple" />
      Allow duplicate characters
    </label>
    <div class="bottom">
      <div class="button-group">
        <div
          class="button"
          v-if="isGardenerOrTorActive || grimoire.isMockAssignmentsAllowed"
          @click="assignRoles"
          :class="{
            disabled:
              selectedRoles !== nonTravellers ||
              !selectedRoles ||
              isIllegalTokenSelected,
          }"
        >
          <font-awesome-icon
            :icon="isGardenerOrTorActive ? 'theater-masks' : 'search'"
          />
          {{
            isGardenerOrTorActive
              ? "Assign " + selectedRoles + " characters randomly"
              : "Mock assignment"
          }}
        </div>
        <div
          class="button"
          v-if="!isGardenerOrTorActive"
          @click="assignAndSendRoles"
          :class="{
            disabled:
              selectedRoles !== nonTravellers ||
              !selectedRoles ||
              isIllegalTokenSelected,
          }"
        >
          <font-awesome-icon icon="people-arrows" />
          Pass out {{ selectedRoles }} characters randomly
        </div>
        <div class="button" @click="selectRandomRoles">
          <font-awesome-icon icon="random" />
          Shuffle characters
        </div>
      </div>
      <div class="illegal warning" v-if="isIllegalTokenSelected">
        <font-awesome-icon icon="exclamation-triangle" />
        <span>
          Warning: there are characters selected cannot be sent to players! You
          will not be able to distribute characters until these characters are
          removed.
        </span>
      </div>
      <div class="setup warning" v-if="hasSelectedSetupRoles">
        <font-awesome-icon icon="exclamation-triangle" />
        <span>
          Warning: there are characters selected that modify the game setup! The
          randomizer does not account for these characters.
        </span>
      </div>
    </div>
  </Modal>
</template>

<script>
import Modal from "./Modal";
import gameJSON from "./../../counts.json";
import Token from "./../Token";
import { mapGetters, mapMutations, mapState } from "vuex";

const randomElement = (arr) => arr[Math.floor(Math.random() * arr.length)];

export default {
  components: {
    Token,
    Modal,
  },
  data: function () {
    return {
      roleSelection: {},
      game: gameJSON,
      allowMultiple: false,
    };
  },
  computed: {
    selectedRoles: function () {
      return Object.values(this.roleSelection)
        .map((roles) => roles.reduce((a, { selected }) => a + selected, 0))
        .reduce((a, b) => a + b, 0);
    },
    hasSelectedSetupRoles: function () {
      return Object.values(this.roleSelection).some((roles) =>
        roles.some((role) => role.selected && role.setup),
      );
    },
    isGardenerOrTorActive: function () {
      return this.npcs.some((npc) => npc.id === "gardener" || npc.id === "tor");
    },
    isIllegalTokenSelected: function () {
      return Object.values(this.roleSelection).some((roles) =>
        roles.some((role) => role.selected && this.isDisabled(role)),
      );
    },
    ...mapState(["grimoire", "roles", "npcs", "modals"]),
    ...mapState("players", ["players"]),
    ...mapGetters({ nonTravellers: "players/nonTravellers" }),
  },
  methods: {
    selectRandomRoles() {
      this.roleSelection = {};
      this.roles.forEach((role) => {
        if (!this.roleSelection[role.team]) {
          this.$set(this.roleSelection, role.team, []);
        }
        this.roleSelection[role.team].push(role);
        this.$set(role, "selected", 0);
      });
      delete this.roleSelection["traveller"];
      const playerCount = Math.max(5, this.nonTravellers);
      const composition = this.game[playerCount - 5];
      Object.keys(composition).forEach((team) => {
        for (let x = 0; x < composition[team]; x++) {
          if (this.roleSelection[team]) {
            const available = this.roleSelection[team].filter(
              (role) => !role.selected,
            );
            if (available.length) {
              randomElement(available).selected = 1;
            }
          }
        }
      });
    },
    assignRoles() {
      if (
        this.selectedRoles !== this.nonTravellers ||
        !this.selectedRoles ||
        this.isIllegalTokenSelected
      ) {
        return;
      }
      // generate list of selected roles and randomize it
      const roles = Object.values(this.roleSelection)
        .map((roles) =>
          roles
            // duplicate roles selected more than once and filter unselected
            .reduce((a, r) => [...a, ...Array(r.selected).fill(r)], []),
        )
        // flatten into a single array
        .reduce((a, b) => [...a, ...b], [])
        .map((a) => [Math.random(), a])
        .sort((a, b) => a[0] - b[0])
        .map((a) => a[1]);
      this.players.forEach((player) => {
        if (player.role.team !== "traveller" && roles.length) {
          const value = roles.pop();
          this.$store.commit("players/update", {
            player,
            property: "role",
            value,
          });
        }
      });
      this.$store.commit("toggleModal", "roles");
    },
    assignAndSendRoles() {
      if (
        this.selectedRoles !== this.nonTravellers ||
        !this.selectedRoles ||
        this.isIllegalTokenSelected
      ) {
        return;
      }
      const popup = this.players.some((player) => !player.connected)
        ? "WARNING: Some players have not yet taken their seats. Are you sure you want to assign and distribute characters?"
        : "Do you want to assign and distribute characters to all players?";
      if (!confirm(popup)) return;
      this.assignRoles();
      this.$store.commit("session/distributeRoles", true);
      setTimeout(
        (() => {
          this.$store.commit("session/distributeRoles", false);
        }).bind(this),
        2000,
      );
    },
    isDisabled(role) {
      return (
        role.special &&
        role.special.some(
          (special) =>
            special.type === "selection" && special.name === "bag-disabled",
        )
      );
    },
    ...mapMutations(["toggleModal"]),
  },
  mounted: function () {
    if (!Object.keys(this.roleSelection).length) {
      this.selectRandomRoles();
    }
  },
  watch: {
    roles() {
      this.selectRandomRoles();
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../vars.scss";

ul.tokens {
  padding-left: 5%;
  li {
    border-radius: 50%;
    width: 5vw;
    margin: 5px;
    filter: brightness(0.5);
    transition: all 250ms;

    @media (orientation: portrait) {
      width: 7vh;
    }

    &.disabled {
      box-shadow:
        0 0 10px gray,
        0 0 10px gray;
      .token {
        filter: grayscale(1);
      }
      .fa-exclamation-triangle {
        color: gold;
      }
    }
    &.selected {
      filter: none;
      .buttons {
        display: flex;
      }
      .fa-exclamation-triangle {
        display: block;
      }
    }
    &.townsfolk:not(.disabled) {
      box-shadow:
        0 0 10px $townsfolk,
        0 0 10px #004cff;
    }
    &.outsider:not(.disabled) {
      box-shadow:
        0 0 10px $outsider,
        0 0 10px $outsider;
    }
    &.minion:not(.disabled) {
      box-shadow:
        0 0 10px $minion,
        0 0 10px $minion;
    }
    &.demon:not(.disabled) {
      box-shadow:
        0 0 10px $demon,
        0 0 10px $demon;
    }
    &.traveller:not(.disabled) {
      box-shadow:
        0 0 10px $traveller,
        0 0 10px $traveller;
    }
    &:hover {
      transform: scale(1.2);
      z-index: 10;
    }
    .fa-exclamation-triangle {
      position: absolute;
      color: red;
      filter: drop-shadow(0 0 3px black) drop-shadow(0 0 3px black);
      top: 5px;
      right: -5px;
      font-size: 150%;
      display: none;
    }
    .buttons {
      display: none;
      position: absolute;
      top: 95%;
      text-align: center;
      width: 100%;
      z-index: 30;
      font-weight: bold;
      filter: drop-shadow(0 0 5px rgba(0, 0, 0, 1));
      span {
        flex-grow: 1;
      }
      svg {
        opacity: 0.25;
        cursor: pointer;
        &:hover {
          opacity: 1;
          color: red;
        }
      }
    }
  }
  .count {
    opacity: 1;
    position: absolute;
    left: 0;
    font-weight: bold;
    font-size: 75%;
    width: 5%;
    display: flex;
    align-items: center;
    justify-content: center;

    @media (orientation: portrait) {
      width: 8%;
    }

    &:after {
      content: " ";
      display: block;
      padding-top: 100%;
    }
    &.townsfolk {
      color: $townsfolk;
    }
    &.outsider {
      color: $outsider;
    }
    &.minion {
      color: $minion;
    }
    &.demon {
      color: $demon;
    }
  }
}

.roles .modal {
  .multiple {
    display: block;
    text-align: center;
    cursor: pointer;
    &.checked,
    &:hover {
      color: red;
    }
    &.checked {
      margin-top: 10px;
    }
    svg {
      margin-right: 5px;
    }
    input {
      display: none;
    }
  }

  .bottom {
    display: flex;
    justify-content: center;
    align-items: center;
    .warning {
      position: absolute;
      bottom: 20px;
      z-index: 10;
      &.setup {
        color: red;
        right: 20px;
      }
      &.illegal {
        color: gold;
        right: 80px;
      }
      svg {
        font-size: 150%;
        vertical-align: middle;
      }
      span {
        display: none;
        text-align: center;
        position: absolute;
        right: -20px;
        bottom: 30px;
        width: 420px;
        background: rgba(0, 0, 0, 0.75);
        padding: 5px;
        border-radius: 10px;
        border: 2px solid black;
      }
      &:hover span {
        display: block;
      }
    }
  }
}
</style>
